import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Archive,
  Bot,
  Building2,
  ExternalLink,
  FileSearch,
  FileText,
  Gavel,
  Home,
  LayoutGrid,
  Mail,
  MessageSquare,
  Scale,
  Wrench,
  Briefcase,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useFirmMode } from "../contexts/FirmModeContext";
import { getSystemUrls } from "../config/systemUrls";
import {
  getVisibleNavGroups,
  isNavItemActive,
} from "../config/yimnakNavigation";
import { trackEvent } from "../lib/analytics";
import YimnakBrandMark from "./YimnakBrandMark";
import "./YimnakNavigationLauncher.css";

const ICON_MAP = {
  home: Home,
  message: MessageSquare,
  file: FileText,
  gavel: Gavel,
  wrench: Wrench,
  archive: Archive,
  services: Briefcase,
  contact: Mail,
  ydim: Bot,
  arsheef: Building2,
  ingestion: Scale,
  legal: FileSearch,
};

const WORKSPACE_SIDEBAR_ROUTES = ["/disc", "/chat", "/pleadings"];
const PANEL_WIDTH = 384;
const VIEWPORT_GAP = 16;

function resolveItemHref(item, systemUrls) {
  if (item.kind === "external" && item.urlKey) {
    return systemUrls[item.urlKey] || "#";
  }
  return item.to || "/";
}

function getSidebarOccupiedRect() {
  if (typeof document === "undefined") return null;
  const sidebar = document.querySelector(".unified-sidebar.open, .unified-sidebar:not(.closed)");
  if (!sidebar) return null;
  const rect = sidebar.getBoundingClientRect();
  if (rect.width < 8) return null;
  return rect;
}

function computePanelStyle(triggerEl, dir, pathname, variant) {
  const rect = triggerEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const isMobile = vw <= 640;
  const panelWidth = Math.min(PANEL_WIDTH, vw - VIEWPORT_GAP * 2);
  const maxHeight = Math.min(vh * 0.7, 512);
  const gap = 8;
  const isRtl = dir === "rtl";

  if (isMobile) {
    return { mode: "sheet" };
  }

  let top = Math.min(rect.bottom + gap, vh - maxHeight - VIEWPORT_GAP);
  top = Math.max(VIEWPORT_GAP, top);

  let left;
  const hasSidebar =
    variant === "workspace" &&
    WORKSPACE_SIDEBAR_ROUTES.some(
      (route) => pathname === route || pathname.startsWith(`${route}/`)
    );
  const sidebarRect = hasSidebar ? getSidebarOccupiedRect() : null;

  if (isRtl) {
    left = rect.left;
    if (left + panelWidth > vw - VIEWPORT_GAP) {
      left = vw - VIEWPORT_GAP - panelWidth;
    }
    if (sidebarRect) {
      const safeRight = sidebarRect.left - gap;
      if (left + panelWidth > safeRight) {
        left = Math.max(VIEWPORT_GAP, safeRight - panelWidth);
      }
    }
  } else {
    left = rect.right - panelWidth;
    if (left < VIEWPORT_GAP) left = VIEWPORT_GAP;
    if (sidebarRect) {
      const safeLeft = sidebarRect.right + gap;
      if (left < safeLeft) {
        left = safeLeft;
      }
      if (left + panelWidth > vw - VIEWPORT_GAP) {
        left = vw - VIEWPORT_GAP - panelWidth;
      }
    }
  }

  return {
    mode: "floating",
    top,
    left,
    width: panelWidth,
    maxHeight,
  };
}

/**
 * DS-2 shared navigation launcher — landing + authenticated workspace.
 * @param {{ variant?: 'landing' | 'workspace'; className?: string }} props
 */
export default function YimnakNavigationLauncher({
  variant = "workspace",
  className = "",
}) {
  const { t } = useTranslation();
  const { dir } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { isFirmMode } = useFirmMode();
  const location = useLocation();
  const systemUrls = useMemo(() => getSystemUrls(), []);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const [panelStyle, setPanelStyle] = useState(null);
  const panelId = useMemo(
    () => `yimnak-nav-panel-${Math.random().toString(36).slice(2, 9)}`,
    []
  );

  const canAccessProtected = isAuthenticated || isFirmMode;

  const groups = useMemo(
    () =>
      getVisibleNavGroups({
        canAccessProtected,
        isFirmMode,
        variant,
      }),
    [canAccessProtected, isFirmMode, variant]
  );

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const refreshPanelPosition = useCallback(() => {
    if (!open || !triggerRef.current) return;
    setPanelStyle(
      computePanelStyle(triggerRef.current, dir, location.pathname, variant)
    );
  }, [open, dir, location.pathname, variant]);

  useEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return undefined;
    }

    refreshPanelPosition();

    const onPointerDown = (event) => {
      const root = triggerRef.current?.closest(".yimnak-nav-launcher");
      const panel = document.getElementById(panelId);
      if (
        root &&
        !root.contains(event.target) &&
        panel &&
        !panel.contains(event.target)
      ) {
        close();
      }
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };

    const onLayout = () => refreshPanelPosition();

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
    };
  }, [open, close, panelId, refreshPanelPosition]);

  const handleInternalClick = (item) => () => {
    close();

    if (item.analyticsEvent) {
      trackEvent(item.analyticsEvent, {
        page: item.analyticsPage || item.to,
        metadata: { place: "nav-launcher" },
      });
    } else if (item.id === "contact") {
      trackEvent("button_click", {
        page: "/contact",
        metadata: { place: "nav-launcher", buttonName: "contact" },
      });
    }
  };

  const renderItem = (item) => {
    const Icon = ICON_MAP[item.iconId] || LayoutGrid;
    const label = t(item.labelKey);
    const description = item.descriptionKey ? t(item.descriptionKey) : null;
    const active = isNavItemActive(location.pathname, item);
    const href = resolveItemHref(item, systemUrls);

    const content = (
      <>
        <span className="yimnak-nav-launcher__icon" aria-hidden="true">
          <Icon size={16} strokeWidth={1.75} />
        </span>
        <span className="yimnak-nav-launcher__text">
          <span className="yimnak-nav-launcher__label">{label}</span>
          {description ? (
            <span className="yimnak-nav-launcher__desc">{description}</span>
          ) : null}
        </span>
        {item.kind === "external" ? (
          <ExternalLink
            className="yimnak-nav-launcher__external"
            size={14}
            aria-hidden="true"
          />
        ) : null}
      </>
    );

    if (item.kind === "external") {
      return (
        <li key={item.id}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
            className="yimnak-nav-launcher__item"
            onClick={close}
          >
            {content}
          </a>
        </li>
      );
    }

    return (
      <li key={item.id}>
        <Link
          to={item.to}
          role="menuitem"
          className={`yimnak-nav-launcher__item${active ? " is-active" : ""}`}
          aria-current={active ? "page" : undefined}
          onClick={handleInternalClick(item)}
        >
          {content}
        </Link>
      </li>
    );
  };

  const panelContent = open ? (
    <>
      <button
        type="button"
        className="yimnak-nav-launcher__backdrop"
        aria-label={t("appLauncher.closeMenu")}
        onClick={close}
      />
      <div
        id={panelId}
        role="menu"
        aria-labelledby={`${panelId}-trigger`}
        className={`yimnak-nav-launcher__panel${
          panelStyle?.mode === "sheet"
            ? " yimnak-nav-launcher__panel--mobile"
            : " yimnak-nav-launcher__panel--floating"
        }`}
        style={
          panelStyle?.mode === "floating"
            ? {
                top: panelStyle.top,
                left: panelStyle.left,
                width: panelStyle.width,
                maxHeight: panelStyle.maxHeight,
              }
            : undefined
        }
      >
        <p className="yimnak-nav-launcher__title">
          <YimnakBrandMark variant="default" size={18} decorative className="yimnak-nav-launcher__title-mark" />
          <span>{t("appLauncher.title")}</span>
        </p>

        {groups.map((group) => (
          <section key={group.id} className="yimnak-nav-launcher__group">
            <h3 className="yimnak-nav-launcher__group-label">
              {t(group.labelKey)}
            </h3>
            <ul className="yimnak-nav-launcher__list">
              {group.items.map(renderItem)}
            </ul>
          </section>
        ))}
      </div>
    </>
  ) : null;

  return (
    <div
      className={`yimnak-nav-launcher yimnak-nav-launcher--${variant} ${className}`.trim()}
      dir={dir}
    >
      <button
        ref={triggerRef}
        type="button"
        id={`${panelId}-trigger`}
        className="yimnak-nav-launcher__trigger"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={t("appLauncher.openMenu")}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={panelId}
      >
        <YimnakBrandMark variant="white" size={20} decorative />
      </button>

      {panelContent && typeof document !== "undefined"
        ? createPortal(panelContent, document.body)
        : null}
    </div>
  );
}
